import React, { useState } from 'react'
import { Component, LocaleCode, LocalizedText } from '../types/component'
import { getLocalizedText } from '../utils/localization'

interface FAQProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
}

interface FAQItem {
  id: string
  question: string | LocalizedText
  answer: string | LocalizedText
}

export function FAQ({ component, locale, style }: FAQProps) {
  const config = component.config as any
  const allowMultiple = config.allowMultiple !== false
  const showIcons = config.showIcons !== false
  const items: FAQItem[] = config.items || [
    {
      id: '1',
      question: 'What is your return policy?',
      answer:
        "We offer a 30-day money-back guarantee on all products. If you're not satisfied with your purchase, you can return it within 30 days for a full refund.",
    },
    {
      id: '2',
      question: 'How long does shipping take?',
      answer:
        'Standard shipping typically takes 5-7 business days. Express shipping options are available at checkout for faster delivery.',
    },
    {
      id: '3',
      question: 'Do you ship internationally?',
      answer:
        'Yes, we ship to most countries worldwide. International shipping costs and delivery times vary by destination.',
    },
    {
      id: '4',
      question: 'How can I track my order?',
      answer:
        "Once your order ships, you'll receive a tracking number via email. You can use this number to track your package on our website or the carrier's site.",
    },
  ]

  const [openItems, setOpenItems] = useState<Set<string>>(new Set())

  const toggleItem = (id: string) => {
    if (allowMultiple) {
      setOpenItems((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(id)) {
          newSet.delete(id)
        } else {
          newSet.add(id)
        }
        return newSet
      })
    } else {
      setOpenItems((prev) => {
        const newSet = new Set<string>()
        if (!prev.has(id)) {
          newSet.add(id)
        }
        return newSet
      })
    }
  }

  const containerStyle: React.CSSProperties = {
    ...style,
    padding: '32px',
    backgroundColor: style.backgroundColor || '#ffffff',
  }

  const maxWidthStyle: React.CSSProperties = {
    maxWidth: '768px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  }

  return (
    <div style={containerStyle}>
      <div style={maxWidthStyle}>
        {items.map((item) => {
          const isOpen = openItems.has(item.id)

          const itemContainerStyle: React.CSSProperties = {
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            transition: 'box-shadow 0.2s',
          }

          const buttonStyle: React.CSSProperties = {
            width: '100%',
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            textAlign: 'left',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }

          const contentStyle: React.CSSProperties = {
            maxHeight: isOpen ? '384px' : '0',
            opacity: isOpen ? 1 : 0,
            overflow: 'hidden',
            transition: 'all 0.2s ease-in-out',
          }

          const innerContentStyle: React.CSSProperties = {
            padding: '0 24px 16px 24px',
            color: '#6b7280',
            borderTop: '1px solid #f3f4f6',
            paddingTop: '16px',
          }

          return (
            <div
              key={item.id}
              style={itemContainerStyle}
              onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)')
              }
            >
              <button
                onClick={() => toggleItem(item.id)}
                style={buttonStyle}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = '#f9fafb')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = 'transparent')
                }
              >
                <span
                  style={{
                    fontWeight: '500',
                    color: '#111827',
                    paddingRight: '16px',
                  }}
                >
                  {getLocalizedText(item.question, locale)}
                </span>
                {showIcons && (
                  <span
                    style={{
                      fontSize: '20px',
                      color: '#6b7280',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      flexShrink: 0,
                    }}
                  >
                    ▼
                  </span>
                )}
              </button>

              <div style={contentStyle}>
                <div style={innerContentStyle}>
                  <p>{getLocalizedText(item.answer, locale)}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
